import { OptionsObject, SnackbarAction, useSnackbar as notistackUseSnackbar } from "notistack";
import React from "react";

import { SnackbarDismissButton } from "./snackbar-dismiss-button";

// enum to specify which action to show. New actions should have their own class like SnackbarDismissButton
export enum SnackbarActionKey {
    DISMISS = "dismiss",
    NONE = "none",
}

const SNACKBAR_ACTION_COMPONENTS = {
    [SnackbarActionKey.DISMISS]: SnackbarDismissButton,
    [SnackbarActionKey.NONE]: undefined,
};

export interface SnackbarInjectedProps {
    enqueueCustomSnackbar: (message: string, options?: unknown) => void;
    enqueueSuccessSnackbar: (message: string, options?: unknown) => void;
    enqueueErrorSnackbar: (message: string, options?: unknown) => void;
    closeSnackbar: (key?: unknown) => void;
}

interface EnqueueSnackbarProps extends OptionsObject {
    autoHideDuration?: number; // to prevent auto-hide use persist prop instead of null
    action?: SnackbarActionKey | SnackbarAction; // enum for shared actions, raw notistack action for one-offs
}

export function useSnackbar() {
    const { enqueueSnackbar, closeSnackbar } = notistackUseSnackbar();

    const enqueueCustomSnackbar = React.useCallback(
        (snackbarText: string, options?: EnqueueSnackbarProps) => {
            const {
                variant = "default",
                action = SnackbarActionKey.DISMISS,
                autoHideDuration = 3000,
                persist,
                ...rest
            } = options || {};
            const shouldPersist = persist ?? variant === "error"; // By default, variant = 'error' does not auto-dismiss

            // Enum keys map to shared components; anything else passes through to notistack
            const snackbarAction =
                typeof action === "string" && Object.prototype.hasOwnProperty.call(SNACKBAR_ACTION_COMPONENTS, action)
                    ? SNACKBAR_ACTION_COMPONENTS[action as SnackbarActionKey]
                    : action;
            return enqueueSnackbar(snackbarText, {
                autoHideDuration,
                variant: variant,
                action: snackbarAction,
                persist: shouldPersist,
                ...rest,
            });
        },
        [enqueueSnackbar],
    );

    const enqueueSuccessSnackbar = React.useCallback(
        (snackbarText: string) => enqueueCustomSnackbar(snackbarText, { variant: "success" }),
        [enqueueCustomSnackbar],
    );
    const enqueueErrorSnackbar = React.useCallback(
        (snackbarText: string) => enqueueCustomSnackbar(snackbarText, { variant: "error" }),
        [enqueueCustomSnackbar],
    );

    return {
        enqueueCustomSnackbar,
        enqueueSuccessSnackbar,
        enqueueErrorSnackbar,
        closeSnackbar,
    };
}

// HOC to support legacy class components
export function withSnackbar(Component: React.ComponentType) {
    return function ComponentWithSnackbar(props: any) {
        const { enqueueCustomSnackbar, enqueueSuccessSnackbar, enqueueErrorSnackbar, closeSnackbar } = useSnackbar();
        return (
            <Component
                {...props}
                enqueueCustomSnackbar={enqueueCustomSnackbar}
                enqueueSuccessSnackbar={enqueueSuccessSnackbar}
                enqueueErrorSnackbar={enqueueErrorSnackbar}
                closeSnackbar={closeSnackbar}
            />
        );
    };
}
